// src/components/directory/ProgramCard.jsx
// Covers: PL-01 (key facts upfront), GV-01 (verification badge), PL-05 (match indicator)

import React from 'react';
import styles from './ProgramCard.module.css';

const CAT_LABELS = {
  mental:'Mental health', financial:'Financial', housing:'Housing',
  employment:'Employment', family:'Family', medical:'Medical & rehab', indigenous:'Indigenous',
};

export default function ProgramCard({ program, isMatch, animationDelay, onClick }) {
  const {
    title, org, desc, category,
    cost, referral, delivery, familyOK,
    province, tags = [], rural, indigenous,
    verified, verifiedDate,
  } = program;

  const costGood     = cost.toLowerCase().includes('free');
  const referralGood = referral.toLowerCase().includes('no referral');

  return (
    <div
      className={`${styles.card} ${isMatch ? styles.match : ''}`}
      style={{ animationDelay: `${animationDelay}ms` }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      aria-label={`View details for ${title}`}
    >
      {isMatch && <div className={styles.matchBadge}>Matches your answers</div>}

      {/* Header row */}
      <div className={styles.head}>
        <span className={`${styles.catPill} ${styles[`cat_${category}`]}`}>
          {CAT_LABELS[category] || category}
        </span>
        <span className={`${styles.verifyBadge} ${verified ? styles.verified : styles.pending}`}>
          <span className={styles.verifyDot} />
          {verified ? `Verified ${verifiedDate ? formatDate(verifiedDate) : ''}` : 'Pending review'}
        </span>
      </div>

      <div className={styles.title}>{title}</div>
      <div className={styles.org}>{org}</div>
      <div className={styles.desc}>{desc}</div>

      {/* Key facts grid — PL-01 */}
      <div className={styles.facts}>
        <Fact label="Cost"          value={cost}     good={costGood} />
        <Fact label="Referral"      value={referral} good={referralGood} warn={!referralGood} />
        <Fact label="Format"        value={delivery} />
        <Fact
          label="Family access"
          value={familyOK ? 'Without Veteran' : 'Requires Veteran'}
          good={familyOK}
        />
      </div>

      {/* Eligibility snippet */}
      {program.eligWho?.length > 0 && (
        <div className={styles.eligSnippet}>
          <strong>Who can access:</strong>{' '}
          {program.eligWho.slice(0, 2).join(', ')}
          {program.eligWho.length > 2 ? ', and others' : ''}
        </div>
      )}

      {/* Tags */}
      <div className={styles.tags}>
        {tags.slice(0, 4).map(t => (
          <span key={t} className={styles.tag}>{t}</span>
        ))}
        {rural && <span className={`${styles.tag} ${styles.tagRural}`}>Rural/remote</span>}
        {indigenous && <span className={`${styles.tag} ${styles.tagIndigenous}`}>Indigenous</span>}
      </div>

      {/* Footer */}
      <div className={styles.foot}>
        <span className={styles.province}>📍 {province}</span>
        <span className={styles.detailLink}>Full details →</span>
      </div>
    </div>
  );
}

function Fact({ label, value, good, warn }) {
  return (
    <div className={styles.fact}>
      <div className={styles.factLabel}>{label}</div>
      <div className={`${styles.factValue} ${good ? styles.good : ''} ${warn ? styles.warn : ''}`}>
        {value}
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}
